import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { Lock, PencilLine, Save, X } from "lucide-react";

/** ------------------- helpers ------------------- */

const MONEY_FIELDS = [
  "cash_amt",
  "eftpos_amt",
  "totalmobility_amt",
  "tm_hoist_amt",
  "discount",
  "account_amt",
  "other_amt",
  "ecard_amt",
] as const;
type MoneyField = (typeof MONEY_FIELDS)[number];

const LABELS: Record<MoneyField, string> = {
  cash_amt: "Cash",
  eftpos_amt: "EFTPOS",
  totalmobility_amt: "Total Mobility",
  tm_hoist_amt: "TM Hoist",
  discount: "Discount",
  account_amt: "Account",
  other_amt: "Other",
  ecard_amt: "eCard",
};

const fmt = (n: number | null | undefined) =>
  typeof n === "number" && !Number.isNaN(n) ? n.toFixed(2) : "0.00";

const parseMoney = (v: string) => {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.max(-999999, Math.min(999999, Math.round(n * 100) / 100));
};

const within24h = (puDate: string | null, puTime?: string | null) => {
  if (!puDate) return false;
  const base = puTime ? new Date(`${puDate}T${puTime}`) : new Date(`${puDate}T00:00:00`);
  const t = base.getTime();
  if (Number.isNaN(t)) return false;
  const now = Date.now();
  return now - t <= 24 * 60 * 60 * 1000;
};

const toYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

/** ------------------- types ------------------- */

interface DriverCompletedPayment {
  id: string;
  taxicaller_job_id: string;
  paid_amount: number;
  driver_name: string;

  cash_amt: number;
  eftpos_amt: number;
  totalmobility_amt: number;
  tm_hoist_amt: number;
  discount: number;
  account_amt: number;
  other_amt: number;
  ecard_amt: number;

  pu_date: string | null; // yyyy-mm-dd
  pu_time: string | null; // HH:MM[:SS] or null

  passenger?: string | null;
  pickup?: string | null;
}

interface CompletedJobsProps {
  driverName: string;            // exact driver_name (e.g. "Kevin McKenzie")
  currentShiftOnly?: boolean;    // kept for API compat
  showValidation?: boolean;      // not used here
}

/** ------------------- component ------------------- */

const CompletedJobs: React.FC<CompletedJobsProps> = ({
  driverName,
  currentShiftOnly = false,
  showValidation = false,
}) => {
  const [rows, setRows] = useState<DriverCompletedPayment[]>([]);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Record<MoneyField, number>>>({});

  useEffect(() => {
    const load = async () => {
      try {
        if (!driverName) {
          console.warn("[CompletedJobs] No driverName prop passed");
          return;
        }
        setLoading(true);

        const today = new Date();
        const yday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const dateMax = toYMD(today);
        const dateMin = toYMD(yday);

        console.log(
          "[CompletedJobs] RPC Filters ->",
          JSON.stringify({ driver_name: driverName, dateMin, dateMax, p_days: 1 })
        );

        const { data, error } = await supabase.rpc("get_recent_dcp_for_driver", {
          p_driver_name: driverName,
          p_days: 1,
        });

        if (error) {
          console.error("[CompletedJobs] RPC fetch error:", error);
          setRows([]);
          return;
        }

        const rpcRows = (data ?? []) as DriverCompletedPayment[];
        console.log(
          "[CompletedJobs] RPC rows count =",
          rpcRows.length,
          "sample =",
          rpcRows.slice(0, 5)
        );
        setRows(rpcRows);
      } catch (err) {
        console.error("[CompletedJobs] Uncaught load error:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [driverName]);

  const totalPaid = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.paid_amount) || 0), 0),
    [rows]
  );

  /** ------------------- edit handlers ------------------- */

  const startEdit = (row: DriverCompletedPayment) => {
    setEditingId(row.id);
    const d: Partial<Record<MoneyField, number>> = {};
    MONEY_FIELDS.forEach((f) => (d[f] = Number(row[f]) || 0));
    console.log("[CompletedJobs] startEdit -> draft:", JSON.stringify(d));
    setDraft(d);
  };

  const cancelEdit = () => {
    console.log("[CompletedJobs] cancelEdit");
    setEditingId(null);
    setDraft({});
  };

  const changeDraft = (field: MoneyField, value: string) => {
    const v = parseMoney(value);
    setDraft((prev) => ({ ...prev, [field]: v }));
  };

  const saveRow = async (row: DriverCompletedPayment) => {
    if (!editingId) return;

    const editable = within24h(row.pu_date, row.pu_time);
    if (!editable) {
      console.warn("[CompletedJobs] saveRow blocked (older than 24h). id=", row.id);
      cancelEdit();
      return;
    }

    // Build values with safe numbers (no undefined)
    const values = {
      p_id: row.id,
      p_driver_name: row.driver_name,
      p_cash: Number(draft.cash_amt ?? 0),
      p_eftpos: Number(draft.eftpos_amt ?? 0),
      p_tm: Number(draft.totalmobility_amt ?? 0),
      p_tm_hoist: Number(draft.tm_hoist_amt ?? 0),
      p_discount: Number(draft.discount ?? 0),
      p_account: Number(draft.account_amt ?? 0),
      p_other: Number(draft.other_amt ?? 0),
      p_ecard: Number(draft.ecard_amt ?? 0),
    };

    console.log("[CompletedJobs] calling update RPC with:", JSON.stringify(values));

    const { data, error } = await supabase.rpc(
      "update_dcp_payment_breakdown",
      values
    );

    if (error) {
      console.error("[CompletedJobs] update RPC error:", error);
      return;
    }

    const updatedRow = (Array.isArray(data) ? data[0] : data) as DriverCompletedPayment | undefined;
    console.log("[CompletedJobs] update RPC returned:", updatedRow);

    if (updatedRow) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updatedRow } : r)));
    } else {
      // In case function returns empty (no row matched 24h window), do nothing
      console.warn("[CompletedJobs] update RPC matched no row (possibly outside 24h window).");
    }

    cancelEdit();
  };

  /** ------------------- render ------------------- */

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl text-yellow-400">
          Completed (Editable for 24h)
        </CardTitle>
        <div className="text-xs text-yellow-300/80">
          You can adjust your payment breakdown for jobs picked up within the last
          24 hours. Older jobs are locked.
        </div>
      </CardHeader>

      <CardContent>
        {loading && <div className="text-sm text-gray-300">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="text-sm text-gray-300">No completed jobs in the last 24 hours.</div>
        )}

        <div className="space-y-4">
          {rows.map((row) => {
            const isEditing = editingId === row.id;
            const canEdit = within24h(row.pu_date, row.pu_time);

            const pickupShort =
              row.pickup && row.pickup.includes(",")
                ? row.pickup.split(",")[0]
                : row.pickup ?? "";

            return (
              <div key={row.id} className="p-3 border rounded-lg bg-[#111315]">
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-yellow-300">
                      Job #{row.taxicaller_job_id}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {row.pu_date ?? "—"} {row.pu_time ?? ""}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!canEdit && (
                      <div
                        className="inline-flex items-center gap-1 text-xs text-gray-400"
                        title="Editing locked after 24 hours"
                      >
                        <Lock size={14} /> Locked
                      </div>
                    )}
                    <span className="font-bold text-green-400">
                      ${fmt(Number(row.paid_amount))}
                    </span>
                  </div>
                </div>

                {/* Passenger & Pickup */}
                {(row.passenger || pickupShort) && (
                  <div className="text-xs text-gray-300 mb-2">
                    {row.passenger && <div>Passenger: {row.passenger}</div>}
                    {pickupShort && <div>Pickup: {pickupShort}</div>}
                  </div>
                )}

                {/* Payment Breakdown */}
                <div className="bg-black/30 p-2 rounded border border-yellow-900/30 text-xs">
                  <div className="font-semibold text-yellow-300 mb-2">Payment Breakdown</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {MONEY_FIELDS.map((f) => (
                      <div key={f} className="flex items-center justify-between gap-2">
                        <span className="text-gray-300">{LABELS[f]}:</span>
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            className="w-28 h-7 bg-black/40 border-yellow-800/50 text-yellow-100"
                            value={draft[f] ?? 0}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [f]: parseMoney(e.target.value) }))}
                          />
                        ) : (
                          <span className="text-gray-100">
                            ${fmt(Number(row[f]))}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-3 flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => saveRow(row)}
                        className="bg-yellow-500 hover:bg-yellow-400 text-black"
                        title="Save"
                      >
                        <Save className="mr-1 h-4 w-4" /> Save
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={cancelEdit}
                        className="bg-gray-700 text-white hover:bg-gray-600"
                        title="Cancel"
                      >
                        <X className="mr-1 h-4 w-4" /> Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => startEdit(row)}
                      disabled={!canEdit}
                      className="bg-yellow-500/90 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black"
                      title={canEdit ? "Edit payment breakdown" : "Locked after 24h"}
                    >
                      <PencilLine className="mr-1 h-4 w-4" /> Edit
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {rows.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="text-yellow-300">Total Fares:</span>
                <span className="text-blue-300">
                  ${fmt(totalPaid)}
                </span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompletedJobs;
